import { Configure, InstantSearch, SearchBoxProps } from 'react-instantsearch-hooks-web';
import { debounce } from 'lodash';
import useGlobalData from '@hooks/useGlobalData';
import Hit from './Hit';
import getAlgoliaUserToken from '@utils/algolia/getAlgoliaUserToken';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import { MultipleQueriesQuery } from '@algolia/client-search';
import InsightsMiddleware from './InsightsMiddleware';
import { StyledSearchBox, StyledHits } from './Search.styles';

const algoliaAppId = process.env.GATSBY_ALGOLIA_APP_ID ?? '';
const algoliaSearchKey = process.env.GATSBY_ALGOLIA_SEARCH_KEY ?? '';
const isPreview = process.env.GATSBY_KONTENT_PREVIEW_MODE_ENABLED === 'true';

// SOURCE https://www.algolia.com/doc/guides/building-search-ui/getting-started/react-hooks/
const algoliaClient = algoliasearch(algoliaAppId, algoliaSearchKey);
const searchClient = {
  ...algoliaClient,
  // NOTE Custom implementation of search() to remove results when no query is typed (!params.query)
  // Otherwise, all results would be displayed even if nothing is typed in the search input.
  search(requests: MultipleQueriesQuery[]) {
    if (requests.every(({ params }) => !params?.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          processingTimeMS: 0,
          query: '',
        })),
      });
    }

    return algoliaClient.search(requests);
  },
} as SearchClient;

const Search = ({ selectedVersion }: { selectedVersion: string }) => {
  const userToken = getAlgoliaUserToken();
  const { searchPlaceholder } = useGlobalData();
  const searchDebounceTime = 250;

  // NOTE We provide a custom queryHook function to debounce the calls to Algolia.
  const queryHook: SearchBoxProps['queryHook'] = debounce((query, search) => {
    search(query);
  }, searchDebounceTime);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={`${isPreview ? 'preview-' : ''}pages-full-${selectedVersion}`}
    >
      <InsightsMiddleware userToken={userToken} />
      <Configure
        attributesToSnippet={['content']}
        clickAnalytics
        hitsPerPage={10}
        userToken={userToken}
      />
      <StyledSearchBox placeholder={searchPlaceholder} queryHook={queryHook} />
      <StyledHits hitComponent={Hit} />
    </InstantSearch>
  );
};

export default Search;
